import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CalendarClock, CheckCircle, AlertTriangle, XCircle, Clock, Loader2, RefreshCw, Scale } from "lucide-react"
import { getSourceUpdates, runSourceUpdates } from "../../../lib/api/api"
import { formatDateTime } from "../../../shared/utils/format"

// Report written by server/src/services/updates/runner.py (backups/sources/updates/latest.json)
type SourceStatus = "ok" | "aggiornato" | "da_verificare" | "rinviato" | "errore"

interface SourceResult {
  source: string
  status: SourceStatus
  message: string
  detail?: Record<string, any>
  error?: string | null
  seconds?: number
}

interface Report {
  started: string
  finished?: string
  apply: boolean
  status: SourceStatus
  sources: SourceResult[]
}

interface HistoryItem {
  started: string
  apply: boolean
  status: SourceStatus
  sources: Record<string, { status: SourceStatus; message: string }>
}

interface UpdatesResponse {
  latest: Report | null
  history: HistoryItem[]
  running: { pid: number; started: string } | null
  schedule: { installed: boolean; hour?: number; minute?: number }
}

// Labels are i18n keys (platform.sourceUpdates.{sources,status,kind}); this map only carries
// the non-textual presentation (colour, icon) that doesn't go through t().
const STATUS_STYLE: Record<SourceStatus, { className: string; icon: React.ReactNode }> = {
  ok: { className: "bg-green-100 text-green-800", icon: <CheckCircle className="w-4 h-4" /> },
  aggiornato: { className: "bg-blue-100 text-blue-800", icon: <RefreshCw className="w-4 h-4" /> },
  da_verificare: { className: "bg-yellow-100 text-yellow-800", icon: <AlertTriangle className="w-4 h-4" /> },
  rinviato: { className: "bg-gray-100 text-gray-700", icon: <Clock className="w-4 h-4" /> },
  errore: { className: "bg-red-100 text-red-800", icon: <XCircle className="w-4 h-4" /> }
}

// "2026-10-01" -> "01.10.2026"
export const formatDay = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "")
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso || ""
}

function StatusBadge({ status }: { status: SourceStatus }) {
  const { t } = useTranslation()
  const resolvedStatus = STATUS_STYLE[status] ? status : "rinviato"
  const s = STATUS_STYLE[resolvedStatus]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.className}`}>
      {s.icon}
      {t(`platform.sourceUpdates.status.${resolvedStatus}`)}
    </span>
  )
}

function SourceDetail({ result }: { result: SourceResult }) {
  const { t } = useTranslation()
  const d = result.detail || {}
  if (result.source === "cantonale") {
    const applied: any[] = d.applied || []
    const reviews: any[] = d.reviews || []
    if (!applied.length && !reviews.length) return null
    return (
      <div className="mt-2 space-y-2 text-sm">
        {applied.length > 0 && (
          <ul className="list-disc ml-5 text-gray-700">
            {applied.map((a) => (
              <li key={`${a.number}-${a.kind}`}>
                <span className="font-mono">{a.number}</span> {a.title} — {t(`platform.sourceUpdates.kind.${a.kind}`, { defaultValue: a.kind })}
                {a.version_date && a.kind !== "GONE"
                  ? <> ({t('platform.sourceUpdates.textInForceSince', { date: formatDay(a.version_date) })})</>
                  : null}
              </li>
            ))}
          </ul>
        )}
        {reviews.length > 0 && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="font-medium text-yellow-800 mb-1">
              {t('platform.sourceUpdates.reviewNeeded')}
            </p>
            <ul className="list-disc ml-5 text-yellow-900">
              {reviews.map((r) => (
                <li key={r.number}>
                  <span className="font-mono">{r.number}</span> {r.title}: <span className="text-xs">{r.reasons}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }
  if (result.source === "federale" || result.source === "trattati") {
    const upcoming: any[] = d.upcoming || []
    const manual: string[] = d.manual_uploads_outdated || []
    if (!upcoming.length && !manual.length) return null
    return (
      <div className="mt-2 text-sm text-gray-700 space-y-1">
        {upcoming.length > 0 && (
          <p>
            {t('platform.sourceUpdates.newVersionsPublished')}{" "}
            {upcoming.map((u) => `${u.abbr} (SR ${u.sr}) dal ${u.dates.split(", ").map(formatDay).join(", ")}`).join("; ")}
          </p>
        )}
        {manual.length > 0 && <p className="text-yellow-800">{t('platform.sourceUpdates.manualUploadsOutdated', { list: manual.join(", ") })}</p>}
      </div>
    )
  }
  if (result.source === "in_arrivo") {
    const items: any[] = d.cantonal || []
    if (!items.length) return null
    return (
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pr-4 py-1 font-medium">{t('platform.sourceUpdates.table.inForceFrom')}</th>
              <th className="pr-4 py-1 font-medium">{t('platform.sourceUpdates.table.act')}</th>
              <th className="pr-4 py-1 font-medium">{t('platform.sourceUpdates.table.type')}</th>
              <th className="py-1 font-medium">{t('platform.sourceUpdates.table.bulletin')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={`${i.number}-${i.kind}-${i.in_force}-${i.bu}`} className="border-t border-gray-100">
                <td className="pr-4 py-1 whitespace-nowrap">{formatDay(i.in_force)}</td>
                <td className="pr-4 py-1">
                  <span className="font-mono">{i.number}</span> {i.title}
                </td>
                <td className="pr-4 py-1 whitespace-nowrap">{String(i.kind).toLowerCase()}</td>
                <td className="py-1 whitespace-nowrap">
                  {/^https?:\/\//i.test(i.bu_url || "") ? (
                    <a href={i.bu_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      {t('platform.sourceUpdates.bulletinAbbr')} {i.bu}
                    </a>
                  ) : (
                    `${t('platform.sourceUpdates.bulletinAbbr')} ${i.bu}`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  return null
}

export default function SourceUpdates() {
  const { t } = useTranslation()
  const [data, setData] = useState<UpdatesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const response = await getSourceUpdates()
      setData(response.data)
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('platform.sourceUpdates.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  // poll while a run is in progress
  useEffect(() => {
    if (!data?.running) return
    const timer = setInterval(load, 10000)
    return () => clearInterval(timer)
  }, [data?.running, load])

  const handleRun = async () => {
    try {
      setStarting(true)
      await runSourceUpdates()
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('platform.sourceUpdates.errors.runFailed'))
    } finally {
      setStarting(false)
    }
  }

  const latest = data?.latest
  const schedule = data?.schedule

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('platform.sourceUpdates.title')}</h2>
          <p className="text-gray-600 mt-1">
            {t('platform.sourceUpdates.subtitle')}
          </p>
        </div>
        <Scale className="w-8 h-8 text-primary-500" />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <CalendarClock className="w-4 h-4" />
        {schedule?.installed ? (
          <span>
            {t('platform.sourceUpdates.autoScheduled')}
            {schedule.hour !== undefined
              ? <> {t('platform.sourceUpdates.atTime', {
                  time: `${String(schedule.hour).padStart(2, "0")}:${String(schedule.minute ?? 0).padStart(2, "0")}`,
                })}</>
              : null}
          </span>
        ) : (
          <span>{t('platform.sourceUpdates.autoUnscheduled')}</span>
        )}
        <button
          onClick={handleRun}
          disabled={starting || !!data?.running}
          className="ml-auto flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {starting || data?.running ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {data?.running ? t('platform.sourceUpdates.runningSince', { time: formatDateTime(data.running.started) }) : t('platform.sourceUpdates.runNow')}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('platform.sourceUpdates.loading')}
        </div>
      ) : !latest ? (
        <p className="text-gray-500 text-sm">{t('platform.sourceUpdates.noRun')}</p>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          <div className="px-4 py-3 flex items-center justify-between bg-gray-50 rounded-t-lg">
            <span className="text-sm text-gray-700">
              {t('platform.lastCheck')} {formatDateTime(latest.started)}
              {!latest.apply && t('platform.sourceUpdates.verifyOnly')}
            </span>
            <StatusBadge status={latest.status} />
          </div>
          {latest.sources.map((s) => (
            <div key={s.source} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-800">{t(`platform.sourceUpdates.sources.${s.source}`, { defaultValue: s.source })}</p>
                  <p className="text-sm text-gray-600">{s.message}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <SourceDetail result={s} />
            </div>
          ))}
        </div>
      )}

      {data && data.history.length > 1 && (
        <details className="text-sm text-gray-600">
          <summary className="cursor-pointer">{t('platform.sourceUpdates.previousRuns')}</summary>
          <ul className="mt-2 space-y-1">
            {data.history.slice(1).map((h) => (
              <li key={h.started} className="flex items-center gap-2">
                <span className="w-36">{formatDateTime(h.started)}</span>
                <StatusBadge status={h.status} />
                <span className="truncate">
                  {Object.entries(h.sources)
                    .filter(([, v]) => v.status !== "ok")
                    .map(([k, v]) => `${k}: ${v.message}`)
                    .join(" · ") || t('platform.sourceUpdates.allAligned')}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
